import { useLoaderData, type LoaderFunctionArgs } from "react-router";

import { SectionRenderer } from "@/components/cms/SectionRenderer";
import { JsonLd } from "@/components/ui/JsonLd";
import { PageHeader } from "@/components/ui/PageHeader";
import { fetchPage, type PageDoc, type SectionDoc } from "@/content";
import { webPageJsonLd } from "@/lib/jsonld";
import { buildMetadata } from "@/lib/seo";

/**
 * A page built entirely in the CMS.
 *
 * The site's own routes are hand-written React and stay that way: they have
 * layout decisions in them that no section type expresses, and a super admin
 * merging sections into them is better than replacing them. This route is for
 * the pages that do not exist yet, so somebody at HISCUS can publish one
 * without a developer.
 *
 * It lives under /p/ rather than at the root so that a CMS page can never
 * shadow a real route. A page slugged "about" would otherwise decide, by
 * matching order, whether visitors saw the About page or a draft somebody was
 * halfway through, and which of the two won would depend on the route table.
 */
export async function loader({ params, request }: LoaderFunctionArgs) {
  const page = await fetchPage(String(params.slug), request.signal);
  if (!page) throw new Response("Not found", { status: 404 });
  return page;
}

export const meta = ({ data }: { data?: unknown }) => {
  const page = data as (PageDoc & { sections: SectionDoc[] }) | undefined;
  if (!page) return null;
  return buildMetadata({
    title: page.metaTitle || page.title,
    description: page.metaDescription || page.lead || `${page.title}, from HISCUS Nepal.`,
    path: `/p/${page.slug}`,
  });
};

export function CmsPage() {
  const page = useLoaderData<PageDoc & { sections: SectionDoc[] }>();

  return (
    <>
      <PageHeader
        page={page}
        eyebrow={page.eyebrow ?? ""}
        title={page.title}
        lead={page.lead ?? ""}
        trail={[
          { name: "Home", href: "/" },
          { name: page.title, href: `/p/${page.slug}` },
        ]}
      />

      {page.sections
        .slice()
        .sort((a, b) => a.order - b.order)
        .map((section) => (
          <SectionRenderer key={section._id ?? `${section.type}-${section.order}`} section={section} />
        ))}

      <JsonLd
        data={webPageJsonLd({
          title: page.title,
          description: page.metaDescription || page.lead,
          path: `/p/${page.slug}`,
          type: page.schemaType ?? "WebPage",
        })}
      />
    </>
  );
}
